import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import type { SheetLayout } from "@/character/grid";
import { startRowLoop } from "@/character/sheet-preview-canvas";
import { getGameSettings } from "@/game/model/game-settings";
import { PET_DISPLAY_ALPHA } from "@/pet/constants";
import { petDisplayScaleMultiplier } from "@/pet/pet-size-scale";
import type { PetDefinition } from "@/pet/types";

const DEFAULT_PREVIEW_ACTION: CharacterActionKey = "idle";

function actionRow(key: CharacterActionKey): number {
  return CHARACTER_ACTIONS.find((a) => a.key === key)?.row ?? 0;
}

function previewFps(key: CharacterActionKey): number {
  if (key === "run") return 12;
  if (key === "attack") return getGameSettings().playerAttackAnimFps;
  return 8;
}

function petSheetLayout(pet: PetDefinition): SheetLayout {
  const grid = pet.grid;
  const sheetWidth = grid.regionX + grid.columns * grid.frameWidth;
  const sheetHeight = grid.regionY + grid.rows * grid.frameHeight;
  return { grid, sheetWidth, sheetHeight };
}

export type PetIdlePreviewOptions = {
  displaySizePx?: number;
  /** Match in-game equipped pet (2× scale, 60% opacity). */
  equippedStyle?: boolean;
  /** IDLE / RUN / SIT / ATTACK buttons under the canvas. */
  showActionButtons?: boolean;
};

export function mountPetSpritePreview(
  host: HTMLElement,
  pet: PetDefinition,
  options?: PetIdlePreviewOptions,
): () => void {
  const basePx = options?.displaySizePx ?? 72;
  const displaySize = options?.equippedStyle
    ? Math.round(basePx * petDisplayScaleMultiplier(pet))
    : basePx;
  const showActionButtons = options?.showActionButtons ?? false;

  const actionButtons = showActionButtons
    ? `<div class="shadow-preview-actions" role="group" aria-label="ลองแอนิเมชัน">
        ${CHARACTER_ACTIONS
          .map(
            (action) =>
              `<button type="button" class="btn btn-ghost btn-xs shadow-preview-action${action.key === DEFAULT_PREVIEW_ACTION ? " shadow-preview-action--active" : ""}" data-shadow-preview-action="${action.key}">${action.templateLabel}</button>`,
          )
          .join("")}
      </div>`
    : "";

  host.innerHTML = `
    <div class="shadow-preview-root">
      <div class="shadow-preview-stage${options?.equippedStyle ? " shadow-preview-stage--equipped" : ""}">
        <canvas class="shadow-preview-canvas" role="img" aria-label="${pet.name} ${DEFAULT_PREVIEW_ACTION}"></canvas>
      </div>
      ${actionButtons}
    </div>
  `;

  const stage = host.querySelector<HTMLElement>(".shadow-preview-stage");
  const canvas = host.querySelector<HTMLCanvasElement>(".shadow-preview-canvas");
  if (options?.equippedStyle && stage) {
    stage.style.opacity = String(PET_DISPLAY_ALPHA);
  }

  let stopLoop: (() => void) | undefined;
  let activeAction: CharacterActionKey = DEFAULT_PREVIEW_ACTION;
  let layout: SheetLayout | null = null;
  let img: HTMLImageElement | null = null;

  const setActiveButton = (key: CharacterActionKey) => {
    host.querySelectorAll<HTMLElement>("[data-shadow-preview-action]").forEach((btn) => {
      btn.classList.toggle(
        "shadow-preview-action--active",
        btn.dataset.petPreviewAction === key,
      );
    });
  };

  const playAction = (key: CharacterActionKey) => {
    if (!canvas || !img || !layout) return;
    stopLoop?.();
    activeAction = key;
    setActiveButton(key);
    canvas.setAttribute("aria-label", `${pet.name} ${key}`);
    const row = actionRow(key);
    const mode = key === "attack" ? "once" : "loop";
    const afterOnce =
      key === "attack"
        ? () => {
            playAction("idle");
          }
        : undefined;
    stopLoop = startRowLoop(canvas, img, layout, row, previewFps(key), mode, afterOnce);
  };

  host.querySelectorAll<HTMLButtonElement>("[data-shadow-preview-action]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.petPreviewAction as CharacterActionKey | undefined;
      if (!key) return;
      playAction(key);
    });
  });

  const probe = new Image();
  probe.decoding = "async";
  probe.onload = () => {
    if (!canvas) return;
    img = probe;
    layout = petSheetLayout(pet);
    canvas.style.width = `${displaySize}px`;
    canvas.style.height = `${displaySize}px`;
    playAction(activeAction);
  };
  probe.src = pet.spritesheetDataUrl;

  return () => {
    stopLoop?.();
    host.replaceChildren();
  };
}

/** Card thumbnail — idle loop only. */
export function mountPetIdlePreview(
  host: HTMLElement,
  pet: PetDefinition,
  options?: PetIdlePreviewOptions,
): () => void {
  return mountPetSpritePreview(host, pet, {
    ...options,
    showActionButtons: false,
  });
}
